import { KeyboardEvent } from 'react';

import { Badge, Button, Collapse, Group, Paper, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { SCUD_STATUS_OPTIONS } from '@/shared/constant/scudMapping';
import {
	IScudLocationAccess,
	IScudMappingReason,
	TScudMappingFilters,
	TScudMappingMode,
	TScudMatchStatus,
} from '@/shared/types/scudMapping.types';
import { LabelInput, MultiSelect } from '@/shared/uikit/components/input';
import { DateField } from '@/shared/uikit/components/picker/DateField/DateField';

import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import { reasonLabel } from './utils';

export const EMPTY_SCUD_FILTERS: TScudMappingFilters = {
	statuses: [],
	locations: [],
	last_pass_from: null,
	last_pass_to: null,
	search: '',
	reason_ids: [],
	confirmed_by: '',
};

type ScudMappingFiltersProps = {
	value: TScudMappingFilters;
	onChange: (value: TScudMappingFilters) => void;
	onApply: () => void;
	onReset: () => void;
	mode: TScudMappingMode;
	locations: IScudLocationAccess[];
	reasons: IScudMappingReason[];
};

/** «Кто подтвердил» нужен только в «Обработанных» и свёрнут, чтобы на ноутбуке таблице оставалось место */
export const ScudMappingFilters = ({
	value,
	onChange,
	onApply,
	onReset,
	mode,
	locations,
	reasons,
}: ScudMappingFiltersProps) => {
	const [isMoreOpened, { toggle: toggleMore }] = useDisclosure(false);

	const set = <K extends keyof TScudMappingFilters>(key: K, item: TScudMappingFilters[K]) =>
		onChange({ ...value, [key]: item });

	const applyOnEnter = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			onApply();
		}
	};

	const isProcessed = mode === 'processed';
	const hiddenCount = Number(!!value.confirmed_by.trim());

	return (
		<Paper withBorder radius="lg" p="sm" bg="var(--mantine-color-gray-0)">
			<Stack gap="sm">
				<Group align="flex-end" gap="sm">
					<LabelInput
						compact
						label="Поиск"
						placeholder="ФИО, пропуск, фирма"
						miw={220}
						style={{ flex: 1 }}
						value={value.search}
						onChange={(event) => set('search', event.currentTarget.value)}
						clearable
						onClear={() => set('search', '')}
						onKeyDown={applyOnEnter}
					/>
					<MultiSelect
						compact
						caption="Статус сопоставления"
						placeholder={value.statuses.length ? undefined : 'Все'}
						w={230}
						data={SCUD_STATUS_OPTIONS}
						value={value.statuses.map(String)}
						onChange={(items) =>
							set(
								'statuses',
								items.map((item) => Number(item) as TScudMatchStatus),
							)
						}
						clearable
					/>
					<MultiSelect
						compact
						caption="Локация"
						placeholder={value.locations.length ? undefined : `Все доступные (${locations.length})`}
						w={220}
						data={locations.map((item) => item.location)}
						value={value.locations}
						onChange={(items) => set('locations', items)}
						searchable
						clearable
					/>
					<DateField
						compact
						type="range"
						label="Последний проход"
						placeholder="с — по"
						w={220}
						value={[value.last_pass_from, value.last_pass_to]}
						onChange={([from, to]) =>
							onChange({ ...value, last_pass_from: from, last_pass_to: to })
						}
						allowSingleDateInRange
						clearable
					/>
					<MultiSelect
						compact
						caption="Причина"
						placeholder={value.reason_ids.length ? undefined : 'Все'}
						w={260}
						data={reasons.map((reason) => ({ value: reason.id, label: reasonLabel(reason) }))}
						value={value.reason_ids}
						onChange={(items) => set('reason_ids', items)}
						searchable
						clearable
					/>
					{isProcessed && (
						<Button
							variant="subtle"
							h={42}
							rightSection={
								isMoreOpened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
							}
							onClick={toggleMore}
							aria-expanded={isMoreOpened}
						>
							Ещё фильтры
							{hiddenCount > 0 && (
								<Badge size="sm" circle ml={6}>
									{hiddenCount}
								</Badge>
							)}
						</Button>
					)}
					<Group gap="xs" ml="auto">
						<Button h={42} onClick={onApply}>
							Применить
						</Button>
						<Button h={42} variant="outline" onClick={onReset}>
							Сбросить
						</Button>
					</Group>
				</Group>
				<Collapse in={isProcessed && isMoreOpened}>
					<Group align="flex-end" gap="sm">
						{isProcessed && (
							<LabelInput
								compact
								label="Кто подтвердил"
								placeholder="Логин"
								w={220}
								value={value.confirmed_by}
								onChange={(event) => set('confirmed_by', event.currentTarget.value)}
								clearable
								onClear={() => set('confirmed_by', '')}
								onKeyDown={applyOnEnter}
							/>
						)}
					</Group>
				</Collapse>
			</Stack>
		</Paper>
	);
};
